<template>
  <main class="view">

    <!-- /diary — flat list of all entries, newest first -->
    <template v-if="route.name === 'Diary'">
      <div class="diary">
        <div v-for="entry in entries" :key="entry.uid" class="diary-page">
          <div
            v-for="(group, gIdx) in makeGroups(entry.blocks)"
            :key="gIdx"
            class="diary-entry"
            :class="{ 'diary-entry--text': !group.main }"
          >
            <template v-if="group.main">
              <div class="diary-visual">
                <template v-if="group.main.type === 'image'">
                  <img
                    v-for="file in (group.main.files || [])"
                    :key="file.filename"
                    :src="file.url"
                    :srcset="file.srcset"
                    sizes="(max-width: 700px) 100vw, 57vw"
                  />
                </template>
                <div v-else-if="group.main.type === 'video' && group.main.content?.url" class="diary-video-wrap">
                  <iframe
                    v-if="embedUrl(group.main.content.url)"
                    :src="embedUrl(group.main.content.url)"
                    frameborder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowfullscreen
                  />
                  <video v-else :src="group.main.content.url" controls playsinline />
                </div>
              </div>
              <div class="diary-meta" :class="{ 'diary-meta--stacked': titleStacked }">
                <div class="diary-meta-header">
                  <span v-if="group.isFirstVisual" class="diary-date noselect">{{ formatDate(entry.date) }}</span>
                  <span v-if="group.isFirstVisual && entry.title" class="diary-caption">{{ entry.title }}</span>
                  <span v-else-if="group.main.content?.caption" class="diary-caption" v-html="textToHtml(group.main.content.caption)"></span>
                </div>
                <div v-for="text in group.texts" :key="text.id" class="diary-desc" v-html="text.content?.text || ''"></div>
              </div>
            </template>
            <template v-else>
              <div class="diary-meta" :class="{ 'diary-meta--stacked': titleStacked }">
                <div v-for="text in group.texts" :key="text.id" class="diary-desc" v-html="text.content?.text || ''"></div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>

    <!-- /diary/:slug — single entry -->
    <template v-else>
      <div class="diary">
        <div
          v-for="(group, idx) in groups"
          :key="idx"
          class="diary-entry"
          :class="{ 'diary-entry--text': !group.main }"
        >
          <template v-if="group.main">
            <div class="diary-visual">
              <template v-if="group.main.type === 'image'">
                <img
                  v-for="file in (group.main.files || [])"
                  :key="file.filename"
                  :src="file.url"
                  :srcset="file.srcset"
                  sizes="(max-width: 700px) 100vw, 57vw"
                />
              </template>
              <div v-else-if="group.main.type === 'video' && group.main.content?.url" class="diary-video-wrap">
                <iframe
                  v-if="embedUrl(group.main.content.url)"
                  :src="embedUrl(group.main.content.url)"
                  frameborder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowfullscreen
                />
                <video v-else :src="group.main.content.url" controls playsinline />
              </div>
            </div>
            <div class="diary-meta" :class="{ 'diary-meta--stacked': titleStacked }">
              <div class="diary-meta-header">
                <span v-if="group.isFirstVisual && kql.page?.date" class="diary-date noselect">{{ kql.page.date }}</span>
                <span v-if="group.main.content?.caption" class="diary-caption" v-html="textToHtml(group.main.content.caption)"></span>
              </div>
              <div v-for="text in group.texts" :key="text.id" class="diary-desc" v-html="text.content?.text || ''"></div>
            </div>
          </template>
          <template v-else>
            <div class="diary-meta" :class="{ 'diary-meta--stacked': titleStacked }">
              <div v-for="text in group.texts" :key="text.id" class="diary-desc" v-html="text.content?.text || ''"></div>
            </div>
          </template>
        </div>
      </div>

      <!-- Older entry: bottom-right -->
      <router-link v-if="nextEntry" :to="'/diary/' + nextEntry.uid" class="diary-entry-next noselect">
        {{ nextEntry.date || nextEntry.title }}
      </router-link>
    </template>

  </main>
</template>

<script setup>
import { useStore } from '@/KQL.js'
import { textToHtml } from '@/utils'

const route = useRoute();
const kql = useStore();

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const formatDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${parseInt(d)} ${MONTHS[parseInt(m) - 1]} ${y}`;
};

const makeGroups = (blocks = []) => {
  const result = [];
  let leadingTexts = [];
  let firstVisualSeen = false;
  for (const block of blocks) {
    if (block.type === 'image' || block.type === 'video') {
      // Leading text blocks attach to the first image; subsequent images start fresh
      result.push({ main: block, texts: firstVisualSeen ? [] : leadingTexts, isFirstVisual: !firstVisualSeen });
      leadingTexts = [];
      firstVisualSeen = true;
    } else if (block.type === 'text') {
      if (!firstVisualSeen) {
        leadingTexts.push(block);
      } else if (result.length > 0 && result[result.length - 1].main) {
        result[result.length - 1].texts.push(block);
      } else {
        result.push({ main: null, texts: [block], isFirstVisual: false });
      }
    }
  }
  // If only text blocks and no image ever appeared
  if (leadingTexts.length) result.push({ main: null, texts: leadingTexts, isFirstVisual: false });
  return result;
};

const titleStacked = computed(() => kql.site?.content?.diarytitlestacked === 'true')

// List view: all entries, newest first
const entries = computed(() =>
  Object.values(kql.page?.children || {}).sort((a, b) => new Date(b.date) - new Date(a.date))
);

// Single entry view
const groups = computed(() => makeGroups(kql.page?.blocks || []));

const siblings = computed(() =>
  route.name === 'DiaryEntry' ? Object.values(kql.page?.siblings || {}) : []
);

const currentIdx = computed(() =>
  siblings.value.findIndex(s => s.uid === (route.params.slug || ''))
);

const nextEntry = computed(() =>
  currentIdx.value < siblings.value.length - 1 ? siblings.value[currentIdx.value + 1] : null
);

const embedUrl = (url) => {
  if (!url) return null;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  return null;
};
</script>

<style>
/* Older entry link: bottom-right */
.diary-entry-next {
  position: fixed;
  right: var(--page-margin, 32px);
  bottom: var(--page-margin, 28px);
  z-index: 5;
  font-style: normal;
  text-align: right;
}

@media only screen and (max-width: 700px) {
  .diary-entry-next {
    right: var(--page-margin, 16px);
    bottom: var(--page-margin, 16px);
  }
}
</style>
